/**
 * FEMA NFHL flood polygons as generation-layout exclusion zones.
 *
 * Fetches Special Flood Hazard Area geometry over a parcel, intersects it
 * with the parcel ring, and snapshots the result as a
 * `GenerationLayoutExclusionSet` for `GenerationLayoutConfiguration`. The
 * snapshot — not a live fetch — is what the deterministic test-fit solver
 * consumes, so published exhibits re-validate byte-identically.
 *
 * Incomplete coverage blocks a complete Test Fit verdict while retaining all
 * verified positive polygons. Missing, conflicting, or malformed rows never
 * become proof that remaining land is flood-free.
 */
import polygonClipping from 'polygon-clipping';

import type {
  GenerationLayoutExclusionSet,
  GenerationLayoutExclusionZone,
} from '../../types/generationImpact';
import { throwIfAborted } from '../../lib/abortSignal';
import {
  annualChanceClass,
  arcgisRingsToMultiPolygon,
  assessFemaFloodCoverage,
  type FemaGeometryTier,
  GEOMETRY_PRECISION_SNAP_DEG,
  GEOMETRY_PRECISION_SNAP_MAX_SHIFT_MM,
  isClippingRingFailure,
  isFemaSourceUnavailable,
  isFloodway,
  queryFemaPolygons,
  snapMultiPolygonPrecision,
  withoutCompletenessClaim,
  type FemaPolygonFeature,
} from './femaFlood';
import type { FloodScreeningCoverage } from '../../types/floodScreening';

type Position = [number, number];
type MultiPolygon = polygonClipping.MultiPolygon;

const FEMA_PROVIDER_ID = 'fema-nfhl';
const FEMA_MSC_URL = 'https://msc.fema.gov/portal/home';
const COORDINATE_DECIMALS = 7;
const AREA_EPSILON = 1e-12;

/** NFHL coverage envelopes: CONUS, Alaska, Hawaii, Puerto Rico/USVI, Guam/CNMI,
 * American Samoa. Sites outside every envelope get `unavailable`, because an
 * empty NFHL response there means "no coverage", never "no flood risk". */
const NFHL_COVERAGE_BBOXES: ReadonlyArray<{
  minLat: number; maxLat: number; minLng: number; maxLng: number;
}> = [
  { minLat: 24.0, maxLat: 49.8, minLng: -125.5, maxLng: -66.5 },
  { minLat: 51.0, maxLat: 71.6, minLng: -180, maxLng: -128.5 },
  { minLat: 18.4, maxLat: 22.5, minLng: -160.9, maxLng: -154.4 },
  { minLat: 17.4, maxLat: 18.7, minLng: -68.1, maxLng: -64.3 },
  { minLat: 13.0, maxLat: 20.7, minLng: 144.5, maxLng: 146.2 },
  { minLat: -14.7, maxLat: -13.8, minLng: -171.2, maxLng: -169.2 },
];

export interface FetchFemaExclusionZonesOptions {
  /** Include 0.2%-annual-chance (500-yr) zones. Defaults to true. */
  include500yr?: boolean;
  signal?: AbortSignal;
  /** Test seam: replaces the live NFHL query. */
  queryPolygons?: typeof queryFemaPolygons;
  /** Test seam: replaces the wall clock recorded in provenance. */
  now?: () => Date;
}

const KIND_ORDER: Record<GenerationLayoutExclusionZone['kind'], number> = {
  floodway: 0,
  'flood-1pct': 1,
  'flood-0.2pct': 2,
  wetland: 3,
  'steep-slope': 4,
  custom: 5,
};

/** Only the kinds this FEMA service can emit. */
const KIND_LABELS: Record<'floodway' | 'flood-1pct' | 'flood-0.2pct', string> = {
  floodway: 'FEMA regulatory floodway',
  'flood-1pct': 'FEMA 1% annual chance (100-yr) flood zone',
  'flood-0.2pct': 'FEMA 0.2% annual chance (500-yr) flood zone',
};

function unavailable(reason: string, include500yr: boolean, retrievedAt: string): GenerationLayoutExclusionSet {
  return {
    zones: [],
    femaStatus: 'unavailable',
    femaUnavailableReason: reason,
    include500yr,
    retrievedAt,
  };
}

function insideNfhlCoverage(boundary: ReadonlyArray<{ lat: number; lng: number }>): boolean {
  return boundary.some(point => NFHL_COVERAGE_BBOXES.some(box => (
    point.lat >= box.minLat && point.lat <= box.maxLat
    && point.lng >= box.minLng && point.lng <= box.maxLng
  )));
}

function closeParcelRing(boundary: ReadonlyArray<{ lat: number; lng: number }>): Position[] | null {
  const ring: Position[] = boundary
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .map(point => [point.lng, point.lat]);
  if (ring.length < 3) return null;
  const first = ring[0]!;
  const last = ring.at(-1)!;
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
  return ring;
}

function roundCoordinate(value: number): number {
  const scale = 10 ** COORDINATE_DECIMALS;
  return Math.round(value * scale) / scale;
}

function ringSignedArea(ring: ReadonlyArray<readonly number[]>): number {
  let twiceArea = 0;
  for (let index = 0; index + 1 < ring.length; index += 1) {
    const current = ring[index]!;
    const next = ring[index + 1]!;
    twiceArea += current[0]! * next[1]! - next[0]! * current[1]!;
  }
  return twiceArea / 2;
}

/** Outer rings only, holes dropped (conservative), rounded, largest first. */
function multiPolygonToLatLngRings(geometry: MultiPolygon): Array<Array<{ lat: number; lng: number }>> {
  return geometry
    .map(polygon => polygon[0])
    .filter((outer): outer is polygonClipping.Ring => outer != null && outer.length >= 4)
    .map(outer => ({
      absArea: Math.abs(ringSignedArea(outer)),
      ring: outer.map(([lng, lat]) => ({
        lat: roundCoordinate(lat),
        lng: roundCoordinate(lng),
      })),
    }))
    .filter(entry => entry.absArea > AREA_EPSILON)
    .sort((left, right) => right.absArea - left.absArea
      || left.ring[0]!.lat - right.ring[0]!.lat
      || left.ring[0]!.lng - right.ring[0]!.lng)
    .map(entry => entry.ring);
}

type FemaExclusionKind = keyof typeof KIND_LABELS;

function classifyFeature(feature: FemaPolygonFeature): FemaExclusionKind | null {
  const attrs = feature.attributes ?? {};
  if (isFemaSourceUnavailable(attrs)) return null;
  const subtype = String(attrs.ZONE_SUBTY ?? '');
  const zone = String(attrs.FLD_ZONE ?? '').trim().toUpperCase();
  if (isFloodway(subtype)) return 'floodway';
  const annualChance = annualChanceClass(zone, subtype, attrs.SFHA_TF === 'T');
  if (annualChance === '1-percent') return 'flood-1pct';
  if (annualChance === '0.2-percent') return 'flood-0.2pct';
  return null;
}

/**
 * Fetch flood-hazard exclusion zones for one parcel polygon. Zones are grouped
 * per hazard kind (floodway / 100-yr / 500-yr), clipped to the parcel, and
 * canonically ordered so the snapshot is stable for identical NFHL responses.
 */
export async function fetchFemaExclusionZones(
  boundary: ReadonlyArray<{ lat: number; lng: number }>,
  options: FetchFemaExclusionZonesOptions = {},
): Promise<GenerationLayoutExclusionSet> {
  const include500yr = options.include500yr ?? true;
  const retrievedAt = (options.now?.() ?? new Date()).toISOString();
  throwIfAborted(options.signal);

  const parcelRing = closeParcelRing(boundary);
  if (!parcelRing || parcelRing.length < 4) {
    return unavailable('parcel geometry is malformed', include500yr, retrievedAt);
  }
  if (!insideNfhlCoverage(boundary)) {
    return unavailable('site is outside FEMA NFHL coverage (non-US site?)', include500yr, retrievedAt);
  }

  const query = options.queryPolygons ?? queryFemaPolygons;
  const response = await query(boundary, options.signal);
  throwIfAborted(options.signal);
  if ('fallbackReason' in response) {
    return unavailable(response.fallbackReason, include500yr, retrievedAt);
  }

  const parcelGeometry = [[parcelRing]] as MultiPolygon;
  const coverage = {
    ...(response.queryAudit.screeningCoverage ?? assessFemaFloodCoverage(boundary, response.features, response.queryAudit.truncated)),
    sourceKind: response.queryAudit.source,
    ...(response.queryAudit.snapshotAuthority ? { sourceAuthority: response.queryAudit.snapshotAuthority } : {}),
    ...(response.queryAudit.snapshotVersion ? { sourceVersion: response.queryAudit.snapshotVersion } : {}),
    ...(response.queryAudit.snapshotVintageDate ? { sourceVintageDate: response.queryAudit.snapshotVintageDate } : {}),
  };
  let geometryFailure = false;
  const byKind = new Map<FemaExclusionKind, MultiPolygon[]>();
  for (const feature of response.features) {
    const kind = classifyFeature(feature);
    if (kind === null) continue;
    if (kind === 'flood-0.2pct' && !include500yr) continue;
    try {
      const rawRings = feature.geometry?.rings;
      const featureGeometry = rawRings ? arcgisRingsToMultiPolygon(rawRings) : null;
      if (!featureGeometry) { geometryFailure = true; continue; }
      const clipped = polygonClipping.intersection(featureGeometry, parcelGeometry) as MultiPolygon;
      const group = byKind.get(kind) ?? [];
      group.push(clipped);
      byKind.set(kind, group);
    } catch { geometryFailure = true; }
  }

  const zones: GenerationLayoutExclusionZone[] = [];
  const fallbackTiers = new Set<FemaGeometryTier>();
  for (const [kind, geometries] of byKind) {
    // The exact pairwise fold is the historical computation. When
    // polygon-clipping cannot reconstruct an output ring, the same set union is
    // retried in one sweep at exact precision, then on the fixed coordinate
    // grid; either fallback is disclosed on the zone and caps the Test Fit
    // status below 'applied'.
    const mergeAndClip = (operands: MultiPolygon[], parcel: MultiPolygon, sweep: boolean): MultiPolygon => {
      let merged: MultiPolygon;
      if (sweep) {
        merged = operands.length === 1 ? operands[0]! : polygonClipping.union(operands[0]!, ...operands.slice(1)) as MultiPolygon;
      } else {
        merged = operands[0]!;
        for (let i = 1; i < operands.length; i++) merged = polygonClipping.union(merged, operands[i]!) as MultiPolygon;
      }
      return polygonClipping.intersection(merged, parcel) as MultiPolygon;
    };
    let clipped: MultiPolygon;
    let geometryTier: FemaGeometryTier | undefined;
    try {
      clipped = mergeAndClip(geometries, parcelGeometry, false);
    } catch (error) {
      // Preserve other independently verified hazard kinds, but withhold fit —
      // except for polygon-clipping's own ring-reconstruction failure.
      if (!isClippingRingFailure(error)) { geometryFailure = true; continue; }
      try {
        try {
          clipped = mergeAndClip(geometries, parcelGeometry, true);
          geometryTier = 'single-sweep';
        } catch (sweepError) {
          if (!isClippingRingFailure(sweepError)) { geometryFailure = true; continue; }
          clipped = mergeAndClip(geometries.map(snapMultiPolygonPrecision), snapMultiPolygonPrecision(parcelGeometry), true);
          geometryTier = 'precision-snap';
        }
      } catch { geometryFailure = true; continue; }
      fallbackTiers.add(geometryTier);
    }
    let polygons: GenerationLayoutExclusionZone['polygons'];
    try {
      polygons = multiPolygonToLatLngRings(clipped);
    } catch { geometryFailure = true; continue; }
    if (polygons.length === 0) {
      // A fallback merge that erased a hazard kind entirely is not trusted.
      if (geometryTier) geometryFailure = true;
      continue;
    }
    zones.push({
      id: `fema-${kind}`,
      label: KIND_LABELS[kind],
      source: 'fema-nfhl',
      kind,
      polygons,
      ...(geometryTier ? { geometryTier } : {}),
      ...(geometryTier === 'precision-snap' ? { precisionSnapDeg: GEOMETRY_PRECISION_SNAP_DEG } : {}),
      provenance: {
        providerId: FEMA_PROVIDER_ID,
        retrievedAt,
        url: FEMA_MSC_URL,
      },
    });
  }

  zones.sort((left, right) => KIND_ORDER[left.kind] - KIND_ORDER[right.kind]
    || left.id.localeCompare(right.id));

  const fallbackNote = fallbackTiers.has('precision-snap')
    ? ` Flood exclusion geometry was merged on a ${GEOMETRY_PRECISION_SNAP_DEG}-degree coordinate grid (each vertex moved by at most ${GEOMETRY_PRECISION_SNAP_MAX_SHIFT_MM} mm) after an exact-precision clipping failure; the fit is not reported as fully applied.`
    : fallbackTiers.size ? ' Flood exclusion geometry was merged in a single sweep after an exact-precision clipping failure; the fit is not reported as fully applied.' : '';
  // The zone-merge tier never downgrades a tier the coverage already carries.
  const mergeTier: FemaGeometryTier | undefined = fallbackTiers.has('precision-snap') ? 'precision-snap' : fallbackTiers.size ? 'single-sweep' : undefined;
  const worstTier: FemaGeometryTier | undefined = coverage.geometryTier === 'precision-snap' || mergeTier === 'precision-snap' ? 'precision-snap' : (coverage.geometryTier ?? mergeTier);
  // A fallback or failure only ever lowers the coverage claim ('unknown' stays
  // unknown) and never keeps a completeness sentence beside a partial status.
  const capped: FloodScreeningCoverage = geometryFailure || mergeTier
    ? {
      ...coverage,
      status: coverage.status === 'unknown' ? 'unknown' : 'partial',
      reason: (geometryFailure ? `Some NFHL flood geometry could not be clipped. ${withoutCompletenessClaim(coverage.reason)}` : withoutCompletenessClaim(coverage.reason)) + fallbackNote,
      ...(worstTier ? { geometryTier: worstTier } : {}),
      ...(worstTier === 'precision-snap' ? { precisionSnapDeg: GEOMETRY_PRECISION_SNAP_DEG } : {}),
    }
    : coverage;
  const complete = capped.status === 'complete';
  return {
    // 'partial' still requires either retained zones or a genuinely partial
    // COVERAGE: a screening that retained nothing stays 'unavailable' rather
    // than telling the reader that known exclusions were retained.
    zones, femaStatus: complete ? 'applied' : zones.length || coverage.status === 'partial' ? 'partial' : 'unavailable',
    femaCoverage: capped,
    ...(!complete ? { femaUnavailableReason: capped.reason } : {}),
    include500yr, retrievedAt,
  };
}
